import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { adminContent } from "@/i18n/admin";
import { useLang } from "@/i18n/language";
import { portalContent, type CurrencyKey, type PathwayKey } from "@/i18n/portal";
import { supabase } from "@/integrations/supabase/client";
import { formatDate, formatDateTime } from "@/lib/utils";

const inputClass =
  "w-full rounded-lg border border-border bg-background/60 px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary";
const cardClass = "rounded-xl border border-border bg-surface p-6";
const btnPrimary =
  "rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50";
const btnGhost =
  "rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-50";

export type CouponRow = {
  id: string;
  code: string;
  description: string | null;
  discount_type: string;
  percent_off: number | null;
  amount_off: number | null;
  currency: string | null;
  pathway: string | null;
  starts_at: string | null;
  expires_at: string | null;
  max_redemptions: number | null;
  times_redeemed: number;
  is_active: boolean;
  created_at: string;
};

type RedemptionRow = {
  id: string;
  coupon_id: string;
  application_id: string | null;
  user_id: string;
  code: string;
  discount_amount: number | null;
  currency: string | null;
  created_at: string;
};

function money(amount: number, currency: CurrencyKey) {
  return new Intl.NumberFormat(currency === "MXN" ? "es-MX" : "en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function CouponsPanel() {
  const { lang } = useLang();
  const t = adminContent[lang].coupons;
  const queryClient = useQueryClient();

  const couponsQuery = useQuery({
    queryKey: ["admin-coupons"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coupons")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as CouponRow[];
    },
  });

  const redemptionsQuery = useQuery({
    queryKey: ["admin-coupon-redemptions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coupon_redemptions")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as RedemptionRow[];
    },
  });

  const profilesQuery = useQuery({
    queryKey: ["admin-profiles-lite"],
    queryFn: async () => {
      const { data, error } = await supabase.from("profiles").select("id, full_name, email");
      if (error) throw error;
      return data;
    },
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ["admin-coupons"] });
    queryClient.invalidateQueries({ queryKey: ["admin-coupon-redemptions"] });
  }

  const coupons = couponsQuery.data ?? [];
  const redemptions = redemptionsQuery.data ?? [];
  const profiles = Object.fromEntries(
    (profilesQuery.data ?? []).map((p) => [p.id, p.full_name ?? p.email ?? p.id.slice(0, 8)]),
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
      <NewCouponForm onCreated={refresh} />
      <div className="space-y-4">
        {coupons.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.empty}</p>
        ) : (
          coupons.map((c) => (
            <CouponCard
              key={c.id}
              coupon={c}
              redemptions={redemptions.filter((r) => r.coupon_id === c.id)}
              names={profiles}
              onChanged={refresh}
            />
          ))
        )}
      </div>
    </div>
  );
}

function NewCouponForm({ onCreated }: { onCreated: () => void }) {
  const { lang } = useLang();
  const t = adminContent[lang].coupons;
  const p = portalContent[lang].portal;
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<"percent" | "amount">("percent");
  const [percentOff, setPercentOff] = useState(10);
  const [amountOff, setAmountOff] = useState(500);
  const [currency, setCurrency] = useState<CurrencyKey>("MXN");
  const [pathway, setPathway] = useState<"" | PathwayKey>("");
  const [startsAt, setStartsAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [busy, setBusy] = useState(false);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    const normalized = code.trim().toUpperCase();
    if (!/^[A-Z0-9-]{3,40}$/.test(normalized)) {
      toast.error(t.invalidCode);
      return;
    }
    if (type === "percent" ? percentOff < 1 || percentOff > 100 : amountOff <= 0) {
      toast.error(t.invalidDiscount);
      return;
    }
    setBusy(true);
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("coupons").insert({
      code: normalized,
      description: description.trim() ? description.trim() : null,
      discount_type: type,
      percent_off: type === "percent" ? Math.round(percentOff) : null,
      amount_off: type === "amount" ? amountOff : null,
      currency: type === "amount" ? currency : null,
      pathway: pathway ? pathway : null,
      starts_at: startsAt ? new Date(`${startsAt}T00:00:00`).toISOString() : null,
      expires_at: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
      max_redemptions: maxRedemptions.trim() ? Number(maxRedemptions) : null,
      created_by: auth.user?.id ?? null,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t.created);
    setCode("");
    setDescription("");
    setMaxRedemptions("");
    onCreated();
  }

  return (
    <form onSubmit={create} className={`${cardClass} h-fit`}>
      <h3 className="text-base font-semibold">{t.heading}</h3>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t.lead}</p>

      <label className="mt-5 block text-xs font-semibold text-muted-foreground">{t.code}</label>
      <input
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder={t.codePlaceholder}
        className={`${inputClass} mt-1.5 tracking-widest uppercase`}
      />

      <label className="mt-4 block text-xs font-semibold text-muted-foreground">
        {t.description}
      </label>
      <input
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={t.descriptionPlaceholder}
        className={`${inputClass} mt-1.5`}
      />

      <label className="mt-4 block text-xs font-semibold text-muted-foreground">{t.type}</label>
      <select
        value={type}
        onChange={(e) => setType(e.target.value as "percent" | "amount")}
        className={`${inputClass} mt-1.5`}
      >
        <option value="percent">{t.typePercent}</option>
        <option value="amount">{t.typeAmount}</option>
      </select>

      {type === "percent" ? (
        <>
          <label className="mt-4 block text-xs font-semibold text-muted-foreground">
            {t.percentOff}
          </label>
          <input
            type="number"
            min={1}
            max={100}
            value={percentOff}
            onChange={(e) => setPercentOff(Number(e.target.value))}
            className={`${inputClass} mt-1.5`}
          />
        </>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground">
              {t.amountOff}
            </label>
            <input
              type="number"
              min={1}
              value={amountOff}
              onChange={(e) => setAmountOff(Number(e.target.value))}
              className={`${inputClass} mt-1.5`}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground">
              {t.currency}
            </label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as CurrencyKey)}
              className={`${inputClass} mt-1.5`}
            >
              <option value="MXN">MXN</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>
      )}

      <label className="mt-4 block text-xs font-semibold text-muted-foreground">{t.pathway}</label>
      <select
        value={pathway}
        onChange={(e) => setPathway(e.target.value as "" | PathwayKey)}
        className={`${inputClass} mt-1.5`}
      >
        <option value="">{t.anyPathway}</option>
        {(["basic", "intermediate", "advanced"] as const).map((key) => (
          <option key={key} value={key}>
            {p.pathways[key]}
          </option>
        ))}
      </select>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-muted-foreground">{t.startsAt}</label>
          <input
            type="date"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            className={`${inputClass} mt-1.5`}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-muted-foreground">{t.expiresAt}</label>
          <input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className={`${inputClass} mt-1.5`}
          />
        </div>
      </div>

      <label className="mt-4 block text-xs font-semibold text-muted-foreground">
        {t.maxRedemptions}
      </label>
      <input
        type="number"
        min={1}
        value={maxRedemptions}
        onChange={(e) => setMaxRedemptions(e.target.value)}
        placeholder={t.unlimited}
        className={`${inputClass} mt-1.5`}
      />
      <p className="mt-1.5 text-[11px] text-muted-foreground">{t.maxRedemptionsHint}</p>

      <button type="submit" disabled={busy} className={`${btnPrimary} mt-5`}>
        {t.create}
      </button>
    </form>
  );
}

function CouponCard({
  coupon,
  redemptions,
  names,
  onChanged,
}: {
  coupon: CouponRow;
  redemptions: RedemptionRow[];
  names: Record<string, string>;
  onChanged: () => void;
}) {
  const { lang } = useLang();
  const t = adminContent[lang].coupons;
  const p = portalContent[lang].portal;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const locale = lang === "es" ? "es-MX" : "en-US";

  const discount =
    coupon.discount_type === "percent"
      ? `${coupon.percent_off}%`
      : money(Number(coupon.amount_off ?? 0), (coupon.currency as CurrencyKey) ?? "MXN");

  const now = Date.now();
  const notStarted = coupon.starts_at ? new Date(coupon.starts_at).getTime() > now : false;
  const expired = coupon.expires_at ? new Date(coupon.expires_at).getTime() < now : false;
  const exhausted =
    coupon.max_redemptions !== null && coupon.times_redeemed >= coupon.max_redemptions;

  const windowLabel =
    coupon.starts_at || coupon.expires_at
      ? `${coupon.starts_at ? formatDate(coupon.starts_at) : "—"} → ${
          coupon.expires_at ? formatDate(coupon.expires_at) : "—"
        }`
      : t.noWindow;

  async function toggleActive() {
    setBusy(true);
    const { error } = await supabase
      .from("coupons")
      .update({ is_active: !coupon.is_active })
      .eq("id", coupon.id);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    onChanged();
  }

  async function remove() {
    if (!window.confirm(t.confirmDelete)) return;
    setBusy(true);
    const { error } = await supabase.from("coupons").delete().eq("id", coupon.id);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t.deleted);
    onChanged();
  }

  return (
    <article className={cardClass}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold tracking-widest uppercase">{coupon.code}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {discount}
            {coupon.pathway
              ? ` · ${p.pathways[coupon.pathway as PathwayKey]}`
              : ` · ${t.anyPathway}`}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t.window}: {windowLabel}
            {expired ? ` · ${t.expired}` : notStarted ? ` · ${t.scheduled}` : ""}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t.usage}: {coupon.times_redeemed}
            {coupon.max_redemptions === null ? ` / ${t.unlimited}` : ` / ${coupon.max_redemptions}`}
          </p>
          {coupon.description && (
            <p className="mt-2 text-xs text-muted-foreground">{coupon.description}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${
              coupon.is_active && !expired && !exhausted
                ? "border-primary/40 bg-primary/10 text-primary"
                : "border-border text-muted-foreground"
            }`}
          >
            {coupon.is_active ? t.active : t.inactive}
          </span>
          <button onClick={toggleActive} disabled={busy} className={btnGhost}>
            {coupon.is_active ? t.deactivate : t.activate}
          </button>
          <button onClick={remove} disabled={busy} className={btnGhost}>
            {t.delete}
          </button>
        </div>
      </div>

      <button onClick={() => setOpen((v) => !v)} className={`${btnGhost} mt-4`}>
        {open ? t.hideRedemptions : `${t.viewRedemptions} (${redemptions.length})`}
      </button>

      {open && (
        <div className="mt-4 border-t border-border pt-4">
          <h4 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
            {t.redemptions}
          </h4>
          {redemptions.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">{t.redemptionsEmpty}</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {redemptions.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground"
                >
                  <span className="text-foreground/85">
                    {names[r.user_id] ?? r.user_id.slice(0, 8)}
                  </span>
                  <span>
                    {formatDateTime(r.created_at)}
                    {r.discount_amount !== null
                      ? ` · ${t.discountApplied}: ${money(
                          Number(r.discount_amount),
                          (r.currency as CurrencyKey) ?? "MXN",
                        )}`
                      : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </article>
  );
}
